import {
    DestroyRef, Directive, ElementRef, Input, OnChanges, OnInit, SimpleChanges, inject
} from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { NgControl } from "@angular/forms";
import { Subscription } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HotkeysHostDirective } from "../../../directives/hotkeys/hotkeys-host.directive";
import { PanelTriggerHostDirective } from "../../../directives/panel-trigger/panel-trigger-host.directive";
import type { AllowInElement } from "../../../services/hotkeys/hotkeys.service";

/**
 * Directive that exposes the NgControl instance to the template via a template reference variable
 * and optionally registers a keyboard shortcut that focuses the host element.
 *
 * This directive provides access to the underlying NgControl (FormControl, FormControlName, or NgModel)
 * for form controls, allowing templates to access control state, errors, and validation status.
 * When `focusHotkey` is supplied, pressing the specified key combination moves focus to the host
 * input element automatically.
 *
 * @example
 * ```html
 * <!-- With formControl and a focus hotkey -->
 * <input mgdEnhancedControl [formControl]="myControl" [focusHotkey]="'alt.s'" #ctrl="mgdEnhancedControl">
 * <div *ngIf="ctrl.ngControl.invalid">Invalid</div>
 *
 * <!-- With formControlName -->
 * <div [formGroup]="formGroup">
 *   <input mgdEnhancedControl formControlName="email" #emailCtrl="mgdEnhancedControl">
 *   <div *ngIf="emailCtrl.ngControl.hasError('email')">Invalid email</div>
 * </div>
 *
 * <!-- With ngModel -->
 * <input mgdEnhancedControl [(ngModel)]="value" #modelCtrl="mgdEnhancedControl">
 * <div *ngIf="modelCtrl.ngControl.touched">Touched</div>
 *
 * <!-- Opting in to the browser's saved-value suggestions — a static `name` is required -->
 * <input mgdEnhancedControl formControlName="query" name="query"
 *     [allowBrowserAutocomplete]="true" #queryControl="mgdEnhancedControl">
 * ```
 *
 * @selector [mgdEnhancedControl]
 * @exportAs mgdEnhancedControl
 */
@Directive({
    selector: "[mgdEnhancedControl]",
    exportAs: "mgdEnhancedControl",
    hostDirectives: [HotkeysHostDirective, PanelTriggerHostDirective],
    host: {
        "[attr.autocomplete]": "allowBrowserAutocomplete ? 'on' : 'off'"
    }
})
export class EnhancedControlDirective<E extends HTMLElement = HTMLElement> implements OnInit, OnChanges {
    /**
     * Whether the browser may offer its own saved-value suggestions for this control.
     *
     * Defaults to `false`, which binds `autocomplete="off"` on the host element. Set it to
     * `true` to bind `autocomplete="on"`.
     *
     * Opting in only renders the attribute — browsers key a control's saved values on its
     * `name` (falling back to `id`), and `formControlName` emits no `name` attribute. An
     * opted-in reactive control must therefore **also** declare a stable static `name`, or the
     * opt-in is a silent no-op. Material's generated `mat-input-<n>` id is assigned by render
     * order and is not stable, so it cannot stand in. This pairing is enforced by the
     * `@shopliftr/angular/require-name-with-browser-autocomplete` ESLint rule.
     *
     * Leave it off for the vast majority of controls: browser suggestion popups overlay
     * Material's own autocomplete and datepicker panels, and can repopulate a control behind
     * `EnhancedFormDirective`'s change tracking.
     *
     * @type {boolean}
     */
    @Input({ transform: coerceBooleanProperty }) allowBrowserAutocomplete = false;

    /**
     * Optional hotkey combination (e.g. `'alt.s'`) that, when pressed, moves focus to
     * the host form-control element. Uses the same key syntax as {@link HotkeysService}.
     * When not set, no shortcut is registered.
     */
    @Input() focusHotkey?: string;

    /**
     * The NgControl instance associated with this form control.
     * Provides access to control state, value, errors, and validation status.
     */
    readonly ngControl = inject(NgControl, { self: true });

    /**
     * The native DOM element of the host control, used for direct DOM interactions such as focusing.
     */
    get nativeElement(): E {
        return this._elementRef.nativeElement;
    }

    /**
     * ElementRef for the host element, used to manage focus and other DOM interactions.
     */
    private readonly _elementRef = inject<ElementRef<E>>(ElementRef);

    /**
     * HotkeysHostDirective composed via `hostDirectives`, used to register keyboard shortcuts.
     */
    private readonly _hotkeys = inject(HotkeysHostDirective, { self: true });

    /**
     * DestroyRef used to automatically unsubscribe from the hotkey observable when the
     * directive is destroyed.
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Tracks the subscription to the hotkey shortcut, so it can be unsubscribed when the hotkey changes
     */
    private _hotkeySubscription?: Subscription;

    ngOnInit(): void {
        this._registerHotkey();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.focusHotkey && !changes.focusHotkey.firstChange) {
            this._registerHotkey();
        }
    }

    /**
     * Focuses the host element of this directive, allowing programmatic control over focus state.
     */
    focus(): void {
        if (this.ngControl.enabled) {
            this.nativeElement.focus();
        }
    }

    /**
     * Unsubscribes from any existing hotkey subscription and re-registers if `focusHotkey` is set.
     * Called from `ngOnInit` and from `ngOnChanges` when `focusHotkey` changes after init.
     */
    private _registerHotkey(): void {
        this._hotkeySubscription?.unsubscribe();
        this._hotkeySubscription = undefined;

        if (!this.focusHotkey) {
            return;
        }

        this._hotkeySubscription = this._hotkeys.addShortcut$({
            keys: this.focusHotkey,
            allowIn: ["INPUT", "SELECT", "TEXTAREA"] as Array<AllowInElement>
        })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this.focus());
    }
}
